/**
 * Printable Shipping Label Component
 * Opens a new window with the shipping label for printing
 */

import { Order as ApiOrder, ShippingLabelResponse } from '../api/orders';

interface PrintableShippingLabelProps {
  order: ApiOrder;
  label: ShippingLabelResponse;
  codWalletId?: string;
  senderName?: string;
  senderPhone?: string;
  senderAddress?: string;
  onAfterPrint?: () => void;
}

/**
 * Opens a new window with the shipping label and triggers print
 * @param order - Order data
 * @param label - Shipping label response
 * @param options - Optional settings (codWalletId, senderName, senderPhone, senderAddress)
 */
export function printShippingLabel(
  order: ApiOrder,
  label: ShippingLabelResponse,
  options?: { codWalletId?: string; senderName?: string; senderPhone?: string; senderAddress?: string }
): void {
  const codWalletId = options?.codWalletId;
  const senderName = options?.senderName;
  const senderPhone = options?.senderPhone;
  const senderAddressValue = options?.senderAddress;

  // Build sender info from settings or defaults
  const senderCompany = senderName || 'HYB MASTER SHOP';
  const senderTitle = senderPhone
    ? `${senderCompany} โทร ${senderPhone}`
    : senderCompany;
  const senderAddr = senderAddressValue || 'ที่อยู่บริษัท';

  // Build recipient info (phone in title, address separate)
  const recipientTitle = order.shippingPhone
    ? `${order.shippingName || 'ลูกค้า'} โทร ${order.shippingPhone}`
    : (order.shippingName || 'ลูกค้า');

  // Build recipient address (without phone)
  const recipientParts = [
    order.shippingAddressLine1,
    order.shippingAddressLine2,
    order.shippingDistrict,
    order.shippingAmphoe,
    order.shippingProvince,
    order.shippingPostalCode,
  ].filter(Boolean);

  const recipientAddress = recipientParts.join(' ');

  // Determine template type based on payment method
  const isQRPayment = ['promptpay', 'mobile_banking', 'wallet'].includes(
    order.paymentMethod || ''
  );
  const templateType = isQRPayment ? 'QR' : 'COD';

  // Get EMS number and order number
  const emsNumber = label?.label?.trackingNumber || 'EMS-XXXXXX';
  const orderNumber = order.orderNumber || order.id;

  // Calculate total quantity
  const items = order.items || [];
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  // Build items HTML
  const itemsHtml = Array.from({ length: 15 }).map((_, index) => {
    const item = items[index];
    return `
      <tr style="height: 18px; border-bottom: 1px solid #e5e7eb;">
        <td style="padding: 2px 8px; overflow: hidden; white-space: nowrap;">${item ? `${index + 1}. ${item.productName}` : ''}</td>
        <td style="padding: 2px 8px; overflow: hidden; white-space: nowrap;">${item?.variantName || ''}</td>
        <td style="padding: 2px 8px; text-align: center;">${item ? item.quantity : ''}</td>
      </tr>
    `;
  }).join('');

  // Build payment type display: COD with optional Wallet ID (white bold text)
  const paymentTypeHtml = templateType === 'QR'
    ? 'ไม่ต้องเก็บเงิน'
    : codWalletId
      ? `COD<br/><span style="font-size: 14px; font-weight: bold;">Wallet ID : ${codWalletId}</span>`
      : 'COD';

  // Create the HTML content
  const htmlContent = `
<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <title>ใบปะหน้าพัสดุ - ${orderNumber}</title>
  <style>
    @page {
      size: A4 landscape;
      margin: 0;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Sarabun', 'Noto Sans Thai', 'Tahoma', sans-serif;
      font-size: 12px;
      width: 297mm;
      height: 210mm;
      padding: 10mm;
    }

    .label-container {
      width: 148.5mm;
    }

    .section {
      border: 1px solid #374151;
      padding: 8px;
      margin-bottom: -1px;
    }

    .section-title {
      font-weight: bold;
      font-size: 14px;
      margin-bottom: 4px;
    }

    .section-content {
      font-size: 11px;
    }

    .ems-section {
      display: flex;
      border: 1px solid #374151;
      border-top: none;
      height: 41mm;
    }

    .ems-left, .ems-right {
      width: 50%;
      display: flex;
      justify-content: center;
    }

    .ems-left {
      border-right: 1px solid #374151;
      align-items: flex-end;
      padding-bottom: 4mm;
    }

    .ems-right {
      align-items: center;
    }

    .ems-right {
      background-color: #000;
      color: #fff;
    }

    .ems-number {
      font-size: 16px;
      font-weight: 500;
    }

    .payment-type {
      font-size: ${codWalletId && templateType === 'COD' ? '36px' : '48px'};
      font-weight: bold;
      text-align: center;
      line-height: 1.3;
    }

    .payment-type.qr {
      font-size: 24px;
    }

    .spacer {
      height: 20mm;
    }

    .items-table {
      border: 1px solid #374151;
      width: 100%;
      border-collapse: collapse;
    }

    .items-table th {
      background: #f3f4f6;
      border-bottom: 1px solid #374151;
      padding: 4px 8px;
      text-align: left;
      font-size: 11px;
    }

    .items-table th:last-child {
      text-align: center;
    }

    .total-row {
      background: #f9fafb;
      border-top: 1px solid #374151;
    }

    .total-row td {
      padding: 4px 8px;
      font-weight: 500;
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="label-container">
    <!-- Sender section -->
    <div class="section" style="min-height: 22mm;">
      <div class="section-title">ผู้ส่ง (From) ${senderTitle}</div>
      <div class="section-content">${senderAddr}</div>
    </div>

    <!-- Recipient section -->
    <div class="section" style="min-height: 22mm; border-top: none;">
      <div class="section-title">ผู้รับ (To) ${recipientTitle}</div>
      <div class="section-content">${recipientAddress}</div>
    </div>

    <!-- EMS section -->
    <div class="ems-section">
      <div class="ems-left">
        <div class="ems-number">${emsNumber}</div>
      </div>
      <div class="ems-right">
        <div class="payment-type ${templateType === 'QR' ? 'qr' : ''}">
          ${paymentTypeHtml}
        </div>
      </div>
    </div>

    <!-- Spacer -->
    <div class="spacer"></div>

    <!-- Items table -->
    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 70%;">ชื่อสินค้า</th>
          <th style="width: 15%;">ตัวเลือกสินค้า</th>
          <th style="width: 15%;">จำนวน</th>
        </tr>
      </thead>
      <tbody>
        ${itemsHtml}
      </tbody>
      <tfoot>
        <tr class="total-row">
          <td style="border-right: 1px solid #374151;">Order No. ${orderNumber}</td>
          <td style="border-right: 1px solid #374151; text-align: center;">จำนวนรวม</td>
          <td style="text-align: center;">${totalQuantity}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <script>
    // Auto print when page loads
    window.onload = function() {
      window.print();
      // Close window after print (or cancel)
      window.onafterprint = function() {
        window.close();
      };
    };
  </script>
</body>
</html>
  `;

  // Open new window and write content
  const printWindow = window.open('', '_blank', 'width=1100,height=800');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
}

// React component wrapper (for backward compatibility)
export function PrintableShippingLabel({ order, label, codWalletId, senderName, senderPhone, senderAddress, onAfterPrint }: PrintableShippingLabelProps) {
  // Trigger print immediately when component mounts
  printShippingLabel(order, label, { codWalletId, senderName, senderPhone, senderAddress });

  // Call onAfterPrint callback
  if (onAfterPrint) {
    setTimeout(onAfterPrint, 500);
  }

  // Return null - we use a popup window instead
  return null;
}
